import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Save, AlertCircle } from "lucide-react";

/**
 * Admin Settings & Configuration Page
 * Manage commission rates, payment methods, notification templates, and feature toggles
 */
export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState("commission");

  // Queries
  const commissionsQuery = trpc.admin.settings.getCommissionRates.useQuery();
  const paymentMethodsQuery = trpc.admin.settings.getPaymentMethods.useQuery();
  const notificationTemplatesQuery = trpc.admin.settings.getNotificationTemplates.useQuery();
  const featureTogglesQuery = trpc.admin.settings.getFeatureToggles.useQuery();

  // Mutations
  const updateCommissionsMutation = trpc.admin.settings.updateCommissionRates.useMutation({
    onSuccess: () => {
      toast.success("Commission rates updated successfully");
      commissionsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update commissions: ${error.message}`);
    },
  });

  const updatePaymentMethodsMutation = trpc.admin.settings.updatePaymentMethods.useMutation({
    onSuccess: () => {
      toast.success("Payment methods updated successfully");
      paymentMethodsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update payment methods: ${error.message}`);
    },
  });

  const updateNotificationsMutation = trpc.admin.settings.updateNotificationTemplates.useMutation({
    onSuccess: () => {
      toast.success("Notification templates updated successfully");
      notificationTemplatesQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update templates: ${error.message}`);
    },
  });

  const updateFeatureTogglesMutation = trpc.admin.settings.updateFeatureToggles.useMutation({
    onSuccess: () => {
      toast.success("Feature toggles updated successfully");
      featureTogglesQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update toggles: ${error.message}`);
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b-3 border-black pb-4">
        <h1 className="font-heading font-bold text-4xl tracking-tight">SETTINGS & CONFIGURATION</h1>
        <p className="text-black/60 mt-2">Manage commission rates, payment methods, notifications, and platform features</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 border-3 border-black bg-white">
          <TabsTrigger value="commission" className="border-r-2 border-black">
            Commission Rates
          </TabsTrigger>
          <TabsTrigger value="payment" className="border-r-2 border-black">
            Payment Methods
          </TabsTrigger>
          <TabsTrigger value="notification" className="border-r-2 border-black">
            Notifications
          </TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
        </TabsList>

        {/* Commission Rates Tab */}
        <TabsContent value="commission" className="space-y-4">
          <CommissionRatesTab
            data={commissionsQuery.data}
            isLoading={commissionsQuery.isLoading}
            isMutating={updateCommissionsMutation.isPending}
            onSave={(rates) => updateCommissionsMutation.mutate(rates)}
          />
        </TabsContent>

        {/* Payment Methods Tab */}
        <TabsContent value="payment" className="space-y-4">
          <PaymentMethodsTab
            data={paymentMethodsQuery.data}
            isLoading={paymentMethodsQuery.isLoading}
            isMutating={updatePaymentMethodsMutation.isPending}
            onSave={(methods) => updatePaymentMethodsMutation.mutate(methods)}
          />
        </TabsContent>

        {/* Notification Templates Tab */}
        <TabsContent value="notification" className="space-y-4">
          <NotificationTemplatesTab
            data={notificationTemplatesQuery.data}
            isLoading={notificationTemplatesQuery.isLoading}
            isMutating={updateNotificationsMutation.isPending}
            onSave={(templates) => updateNotificationsMutation.mutate(templates)}
          />
        </TabsContent>

        {/* Feature Toggles Tab */}
        <TabsContent value="features" className="space-y-4">
          <FeatureTogglesTab
            data={featureTogglesQuery.data}
            isLoading={featureTogglesQuery.isLoading}
            isMutating={updateFeatureTogglesMutation.isPending}
            onSave={(toggles) => updateFeatureTogglesMutation.mutate(toggles)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Commission Rates Tab Component
 */
function CommissionRatesTab({
  data,
  isLoading,
  isMutating,
  onSave,
}: {
  data?: Record<string, any>;
  isLoading: boolean;
  isMutating: boolean;
  onSave: (rates: any) => void;
}) {
  const [defaultRate, setDefaultRate] = useState(data?.default?.value || 0);

  if (isLoading) {
    return (
      <Card className="border-3 border-black">
        <CardContent className="pt-6 flex items-center justify-center h-40">
          <Loader2 className="animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-3 border-black">
      <CardHeader className="border-b-3 border-black">
        <CardTitle>Commission Rate Configuration</CardTitle>
        <CardDescription>Set default and game-specific commission rates</CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {/* Default Commission Rate */}
        <div className="space-y-2">
          <label className="font-heading font-bold text-sm tracking-tight">Default Commission Rate (%)</label>
          <div className="flex gap-2">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={defaultRate}
              onChange={(e) => setDefaultRate(parseFloat(e.target.value))}
              className="border-2 border-black font-mono"
              placeholder="Enter percentage (0-100)"
            />
            <span className="text-black/60 font-mono text-sm flex items-center">%</span>
          </div>
          <p className="text-xs text-black/50">Applied to all games unless overridden</p>
        </div>

        {/* Info Box */}
        <div className="border-2 border-black bg-black/5 p-4 space-y-2">
          <div className="flex gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-mono text-xs font-bold">Game-Specific Rates</p>
              <p className="text-xs text-black/60 mt-1">
                You can set custom commission rates for individual games. Contact support to configure game-specific rates.
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t-2 border-black">
          <Button
            onClick={() => onSave({ default: defaultRate })}
            disabled={isMutating}
            className="bg-black text-white border-2 border-black font-heading font-bold tracking-tight"
          >
            {isMutating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Payment Methods Tab Component
 */
function PaymentMethodsTab({
  data,
  isLoading,
  isMutating,
  onSave,
}: {
  data?: Record<string, any>;
  isLoading: boolean;
  isMutating: boolean;
  onSave: (methods: any) => void;
}) {
  const [stripeEnabled, setStripeEnabled] = useState(data?.stripe?.enabled ?? true);
  const [stripeTestMode, setStripeTestMode] = useState(data?.stripe?.testMode ?? true);
  const [wepayEnabled, setWepayEnabled] = useState(data?.wepay?.enabled ?? true);
  const [linePayEnabled, setLinePayEnabled] = useState(data?.linepay?.enabled ?? false);

  if (isLoading) {
    return (
      <Card className="border-3 border-black">
        <CardContent className="pt-6 flex items-center justify-center h-40">
          <Loader2 className="animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-3 border-black">
      <CardHeader className="border-b-3 border-black">
        <CardTitle>Payment Method Configuration</CardTitle>
        <CardDescription>Enable/disable payment methods and set test modes</CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {/* Stripe */}
        <div className="border-2 border-black p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-heading font-bold">Stripe PromptPay</p>
              <p className="text-xs text-black/60">Credit card and PromptPay payments</p>
            </div>
            <input
              type="checkbox"
              checked={stripeEnabled}
              onChange={(e) => setStripeEnabled(e.target.checked)}
              className="w-5 h-5 border-2 border-black cursor-pointer"
            />
          </div>
          {stripeEnabled && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={stripeTestMode}
                onChange={(e) => setStripeTestMode(e.target.checked)}
                className="w-4 h-4 border-2 border-black"
              />
              <span className="text-sm font-mono">Test Mode (Sandbox)</span>
            </label>
          )}
        </div>

        {/* wePay */}
        <div className="border-2 border-black p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-heading font-bold">wePay</p>
              <p className="text-xs text-black/60">Bank transfer and mobile payments</p>
            </div>
            <input
              type="checkbox"
              checked={wepayEnabled}
              onChange={(e) => setWepayEnabled(e.target.checked)}
              className="w-5 h-5 border-2 border-black cursor-pointer"
            />
          </div>
        </div>

        {/* LINE Pay */}
        <div className="border-2 border-black p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-heading font-bold">LINE Pay</p>
              <p className="text-xs text-black/60">LINE wallet and LINE points</p>
            </div>
            <input
              type="checkbox"
              checked={linePayEnabled}
              onChange={(e) => setLinePayEnabled(e.target.checked)}
              className="w-5 h-5 border-2 border-black cursor-pointer"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t-2 border-black">
          <Button
            onClick={() =>
              onSave({
                stripe: { enabled: stripeEnabled, testMode: stripeTestMode },
                wepay: { enabled: wepayEnabled },
                linePay: { enabled: linePayEnabled, testMode: false },
              })
            }
            disabled={isMutating}
            className="bg-black text-white border-2 border-black font-heading font-bold tracking-tight"
          >
            {isMutating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Notification Templates Tab Component
 */
function NotificationTemplatesTab({
  data,
  isLoading,
  isMutating,
  onSave,
}: {
  data?: Record<string, any>;
  isLoading: boolean;
  isMutating: boolean;
  onSave: (templates: any) => void;
}) {
  const [topupSuccess, setTopupSuccess] = useState(
    data?.topup_success || "Your game top-up of {amount} C has been completed successfully!"
  );
  const [topupFailure, setTopupFailure] = useState(
    data?.topup_failure || "Your game top-up failed. {amount} C has been refunded to your account."
  );
  const [creditSuccess, setCreditSuccess] = useState(
    data?.credit_purchase_success || "You have successfully purchased {amount} C. Your new balance is {balance} C."
  );
  const [creditFailure, setCreditFailure] = useState(
    data?.credit_purchase_failure || "Your credit purchase failed. Please try again."
  );

  if (isLoading) {
    return (
      <Card className="border-3 border-black">
        <CardContent className="pt-6 flex items-center justify-center h-40">
          <Loader2 className="animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-3 border-black">
      <CardHeader className="border-b-3 border-black">
        <CardTitle>Notification Templates</CardTitle>
        <CardDescription>Customize messages sent to users for various events</CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {/* Top-up Success */}
        <div className="space-y-2">
          <label className="font-heading font-bold text-sm tracking-tight">Top-up Success Message</label>
          <textarea
            value={topupSuccess}
            onChange={(e) => setTopupSuccess(e.target.value)}
            className="w-full border-2 border-black p-3 font-mono text-sm resize-none"
            rows={3}
            placeholder="Message template..."
          />
          <p className="text-xs text-black/50">Available variables: {"{amount}"}, {"{gameName}"}, {"{date}"}</p>
        </div>

        {/* Top-up Failure */}
        <div className="space-y-2">
          <label className="font-heading font-bold text-sm tracking-tight">Top-up Failure Message</label>
          <textarea
            value={topupFailure}
            onChange={(e) => setTopupFailure(e.target.value)}
            className="w-full border-2 border-black p-3 font-mono text-sm resize-none"
            rows={3}
            placeholder="Message template..."
          />
          <p className="text-xs text-black/50">Available variables: {"{amount}"}, {"{gameName}"}, {"{reason}"}</p>
        </div>

        {/* Credit Purchase Success */}
        <div className="space-y-2">
          <label className="font-heading font-bold text-sm tracking-tight">Credit Purchase Success Message</label>
          <textarea
            value={creditSuccess}
            onChange={(e) => setCreditSuccess(e.target.value)}
            className="w-full border-2 border-black p-3 font-mono text-sm resize-none"
            rows={3}
            placeholder="Message template..."
          />
          <p className="text-xs text-black/50">Available variables: {"{amount}"}, {"{balance}"}, {"{date}"}</p>
        </div>

        {/* Credit Purchase Failure */}
        <div className="space-y-2">
          <label className="font-heading font-bold text-sm tracking-tight">Credit Purchase Failure Message</label>
          <textarea
            value={creditFailure}
            onChange={(e) => setCreditFailure(e.target.value)}
            className="w-full border-2 border-black p-3 font-mono text-sm resize-none"
            rows={3}
            placeholder="Message template..."
          />
          <p className="text-xs text-black/50">Available variables: {"{reason}"}, {"{date}"}</p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t-2 border-black">
          <Button
            onClick={() =>
              onSave({
                topupSuccess,
                topupFailure,
                creditPurchaseSuccess: creditSuccess,
                creditPurchaseFailure: creditFailure,
              })
            }
            disabled={isMutating}
            className="bg-black text-white border-2 border-black font-heading font-bold tracking-tight"
          >
            {isMutating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Feature Toggles Tab Component
 */
function FeatureTogglesTab({
  data,
  isLoading,
  isMutating,
  onSave,
}: {
  data?: Record<string, boolean>;
  isLoading: boolean;
  isMutating: boolean;
  onSave: (toggles: any) => void;
}) {
  const [maintenanceMode, setMaintenanceMode] = useState(data?.maintenanceMode ?? false);
  const [newSignups, setNewSignups] = useState(data?.newUserSignups ?? true);
  const [stripePayments, setStripePayments] = useState(data?.stripePayments ?? true);
  const [wepayPayments, setWepayPayments] = useState(data?.wepayPayments ?? true);
  const [lineNotifications, setLineNotifications] = useState(data?.lineNotifications ?? true);
  const [emailNotifications, setEmailNotifications] = useState(data?.emailNotifications ?? true);

  if (isLoading) {
    return (
      <Card className="border-3 border-black">
        <CardContent className="pt-6 flex items-center justify-center h-40">
          <Loader2 className="animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-3 border-black">
      <CardHeader className="border-b-3 border-black">
        <CardTitle>Feature Toggles</CardTitle>
        <CardDescription>Enable or disable platform features</CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {/* Maintenance Mode */}
        <ToggleItem
          label="Maintenance Mode"
          description="Disable all user access, show maintenance message"
          checked={maintenanceMode}
          onChange={setMaintenanceMode}
          warning={maintenanceMode}
        />

        {/* New User Signups */}
        <ToggleItem
          label="New User Signups"
          description="Allow new users to create accounts"
          checked={newSignups}
          onChange={setNewSignups}
        />

        {/* Stripe Payments */}
        <ToggleItem
          label="Stripe Payments"
          description="Enable Stripe PromptPay payment processing"
          checked={stripePayments}
          onChange={setStripePayments}
        />

        {/* wePay Payments */}
        <ToggleItem
          label="wePay Payments"
          description="Enable wePay payment processing"
          checked={wepayPayments}
          onChange={setWepayPayments}
        />

        {/* LINE Notifications */}
        <ToggleItem
          label="LINE Notifications"
          description="Send transaction notifications via LINE"
          checked={lineNotifications}
          onChange={setLineNotifications}
        />

        {/* Email Notifications */}
        <ToggleItem
          label="Email Notifications"
          description="Send transaction notifications via email"
          checked={emailNotifications}
          onChange={setEmailNotifications}
        />

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t-2 border-black">
          <Button
            onClick={() =>
              onSave({
                maintenanceMode,
                newUserSignups: newSignups,
                stripePayments,
                wepayPayments,
                lineNotifications,
                emailNotifications,
              })
            }
            disabled={isMutating}
            className="bg-black text-white border-2 border-black font-heading font-bold tracking-tight"
          >
            {isMutating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Toggle Item Component
 */
function ToggleItem({
  label,
  description,
  checked,
  onChange,
  warning,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  warning?: boolean;
}) {
  return (
    <div
      className={`border-2 ${warning ? "border-red-500 bg-red-50" : "border-black"} p-4 flex items-center justify-between`}
    >
      <div>
        <p className="font-heading font-bold text-sm">{label}</p>
        <p className="text-xs text-black/60">{description}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 border-2 border-black cursor-pointer"
      />
    </div>
  );
}
